// Ubicación en el repo: src/lib/examReport.jsx
// "🖨 Imprimir informe" de audiometría — replica printExamReport() del
// prototipo (línea ~2151): mismo orden y disposición exactos — membrete en
// grid de 3 columnas (logo | título | tagline), datos del paciente,
// audiograma a la izquierda con diapasones+PTP apilados a la derecha,
// logoaudiometría + curva, observaciones y firma. Solo el tamaño de fuente
// y los paddings se agrandaron respecto al prototipo (a pedido del
// usuario, la orientación/estructura debe quedar igual).
// Los audiogramas se reutilizan como componentes React (AudiogramSVG /
// SpeechCurveSVG) renderizados a string con react-dom/server, para no
// duplicar la lógica de dibujo que ya usa la vista de detalle. El SVG
// interno de esos componentes trae un tope de max-width:580px pensado
// para el modal en pantalla; para la impresión ese tope se agranda un
// poco (ver ajuste de tamaño más abajo).
//
// El logo (DECISIONS.md #28 y #39) va en la celda izquierda del membrete,
// junto al tagline — no lo reemplaza. `logoUrl` es una signed URL ya
// resuelta por el caller (ver getPatientBranchLogoUrl en branchLogos.js),
// null si la sucursal del paciente no tiene logo cargado.
import { renderToStaticMarkup } from 'react-dom/server';
import { calcPTA, ptaLabel } from './exams';
import { esc, printHTML } from './print';
import { calculateAge, formatDate, formatPatientName, formatProfessionalName } from './format';
import AudiogramSVG from '../components/AudiogramSVG';
import SpeechCurveSVG from '../components/SpeechCurveSVG';

const cell = (v) => (v !== null && v !== undefined && v !== '' ? esc(String(v)) : '—');
const diapCell = (v) => (v !== null && v !== undefined && v !== '' ? esc(String(v)) : 'NR');

// Ajuste de tamaño para impresión: quita el tope de 580px pensado para el
// modal en pantalla y lo reemplaza por uno más grande. Como es SVG con
// viewBox, todo (líneas, símbolos, texto) escala proporcionalmente — no
// se pixela ni se desalinea.
function enlargeForPrint(svgMarkup, maxWidthPx) {
  return svgMarkup.replace(/max-width:\s*580px/, `max-width:${maxWidthPx}px`);
}

// Extraído a función propia (22-sep-2026, DECISIONS.md #119) para poder
// reutilizar exactamente el mismo HTML del informe impreso al generar el
// PDF adjunto del correo a pacientes (pdfExport.js + examEmail.js) — antes
// este armado vivía solo dentro de printExamReport(), sin forma de
// reutilizarlo sin duplicar toda la plantilla.
export function buildExamReportHTML(exam, logoUrl, signatureUrl) {
  const p = exam.patients || {};
  const prof = exam.professionals;
  const ptaMode = exam.pta_mode ?? 4;
  const ptaAirOd = calcPTA(exam.od, ptaMode, exam.od_nr);
  const ptaAirOi = calcPTA(exam.oi, ptaMode, exam.oi_nr);
  const ptaBoneOd = calcPTA(exam.bone_od, ptaMode, exam.bone_od_nr);
  const ptaBoneOi = calcPTA(exam.bone_oi, ptaMode, exam.bone_oi_nr);

  const logoOd = exam.logo_od || {};
  const logoOi = exam.logo_oi || {};
  const hasLogo = (logoOd.percent != null && logoOd.db != null) || (logoOi.percent != null && logoOi.db != null);

  const r = exam.rinne || {};
  const w = exam.weber || {};

  const audiogramSVG = enlargeForPrint(
    renderToStaticMarkup(
      <AudiogramSVG
        od={exam.od}
        oi={exam.oi}
        boneOd={exam.bone_od}
        boneOi={exam.bone_oi}
        ldlOd={exam.ldl_od}
        ldlOi={exam.ldl_oi}
        odMasked={exam.od_masked}
        oiMasked={exam.oi_masked}
        boneOdMasked={exam.bone_od_masked}
        boneOiMasked={exam.bone_oi_masked}
        odNr={exam.od_nr}
        oiNr={exam.oi_nr}
        boneOdNr={exam.bone_od_nr}
        boneOiNr={exam.bone_oi_nr}
      />
    ),
    900
  );
  // 420 (antes 600): el usuario pidió achicar este gráfico en vez del logo
  // para recuperar el alto que hacía pasar la firma a una segunda hoja —
  // ver DECISIONS.md #39.
  const speechSVG = hasLogo
    ? enlargeForPrint(renderToStaticMarkup(<SpeechCurveSVG logoOd={logoOd} logoOi={logoOi} />), 420)
    : '';

  // Firma: uno u otro, nunca los dos juntos (pedido explícito del usuario,
  // ver DECISIONS.md #49/#50). Si el profesional tiene firma digital
  // cargada, se asume que la imagen ya trae nombre/RUT/timbre como
  // corresponde a una firma y timbre profesional escaneada — mostrar
  // ADEMÁS el nombre/RUT tipeados duplicaría ese dato. Si no tiene firma
  // digital, se usa el formato genérico de siempre: nombre, RUT, línea en
  // blanco para firmar a mano.
  const signatureBlockHTML = signatureUrl
    ? `<div style="margin-top:16px; display:flex; justify-content:center;">
         <img src="${esc(signatureUrl)}" style="max-height:90px; max-width:280px; object-fit:contain; display:block;" />
       </div>`
    : `<div style="margin-top:30px; display:flex; justify-content:center;">
         <div style="text-align:center; width:320px;">
           <div style="border-top:1px solid #333; padding-top:6px; font-size:13.5px;">
             ${prof ? cell(formatProfessionalName(prof, { withRole: true })) : '&nbsp;'}
           </div>
           ${prof?.rut ? `<div style="font-size:12px; color:#444; margin-top:1px;">RUT ${esc(prof.rut)}</div>` : ''}
           <div style="font-size:11px; color:#666; margin-top:2px; font-weight:700; letter-spacing:.03em;">NOMBRE Y FIRMA PROFESIONAL</div>
         </div>
       </div>`;

  const html = `
    <div style="font-family:'Inter',Arial,sans-serif; padding:10px 4px; width:100%; max-width:210mm; font-size:14px; color:#1a1a1a; box-sizing:border-box;">
      <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; border-bottom:2px solid #333; padding-bottom:6px; margin-bottom:12px;">
        <div>${logoUrl ? `<img src="${esc(logoUrl)}" style="max-height:44px; max-width:140px; object-fit:contain;" />` : ''}</div>
        <h2 style="font-family:'Space Grotesk',Arial,sans-serif; margin:0; font-size:21px; letter-spacing:.03em; text-align:center; white-space:nowrap;">EXAMEN AUDIOMÉTRICO</h2>
        <div style="color:#666; font-size:13px; text-align:right;">by GlobalVision</div>
      </div>

      <table style="width:100%; font-size:14px; margin-bottom:12px; border-collapse:collapse;">
        <tr>
          <td style="padding:4px 0; width:60%;"><strong>NOMBRE:</strong> ${cell(formatPatientName(p))}</td>
          <td style="padding:4px 0;"><strong>RUT:</strong> ${cell(p.rut)}</td>
        </tr>
        <tr>
          <td style="padding:4px 0;"><strong>EDAD:</strong> ${p.birthdate ? `${calculateAge(p.birthdate)} años` : '—'}</td>
          <td style="padding:4px 0;"><strong>FECHA:</strong> ${esc(formatDate(exam.date))}</td>
        </tr>
        <tr>
          <td style="padding:4px 0;" colspan="2"><strong>EQUIPO:</strong> ${cell(exam.equipment)}</td>
        </tr>
      </table>

      <div style="display:flex; gap:14px; align-items:flex-start; width:100%;">
        <div style="flex:1.6; min-width:0;">
          ${audiogramSVG}
          <div style="font-size:11px; color:#666; margin-top:4px;"><span style="color:#C4453C; font-weight:700;">OD</span>: ○ aérea (△ enmasc.) · &lt; ósea ([ enmasc.) · ▷ LDL &nbsp; <span style="color:#2563A6; font-weight:700;">OI</span>: × aérea (□ enmasc.) · &gt; ósea (] enmasc.) · ◁ LDL</div>
        </div>
        <div style="flex:1; min-width:0; display:flex; flex-direction:column; gap:14px;">
          <div>
            <div style="font-size:13px; font-weight:700; text-decoration:underline; margin-bottom:5px;">DIAPASONES</div>
            <div style="display:flex; gap:10px;">
              <table style="border-collapse:collapse; font-size:13px; flex:1;">
                <thead><tr><th style="border:1px solid #999; padding:3px 6px; color:#C4453C;">OD</th><th style="border:1px solid #999; padding:3px 6px;">Rinne</th><th style="border:1px solid #999; padding:3px 6px; color:#2563A6;">OI</th></tr></thead>
                <tbody>
                  <tr><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(r.od250)}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">250 Hz</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(r.oi250)}</td></tr>
                  <tr><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(r.od500)}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">500 Hz</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(r.oi500)}</td></tr>
                </tbody>
              </table>
              <table style="border-collapse:collapse; font-size:13px; flex:1;">
                <thead><tr><th style="border:1px solid #999; padding:3px 6px; color:#C4453C;">OD</th><th style="border:1px solid #999; padding:3px 6px;">Weber</th><th style="border:1px solid #999; padding:3px 6px; color:#2563A6;">OI</th></tr></thead>
                <tbody>
                  <tr><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(w.od250)}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">250 Hz</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(w.oi250)}</td></tr>
                  <tr><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(w.od500)}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">500 Hz</td><td style="border:1px solid #999; padding:3px 6px; text-align:center;">${diapCell(w.oi500)}</td></tr>
                </tbody>
              </table>
            </div>
            <div style="font-size:10.5px; color:#888; margin-top:4px;">NR = No realizado</div>
          </div>
          <div>
            <div style="font-size:13px; font-weight:700; text-decoration:underline; margin-bottom:5px;">PROMEDIO ${ptaLabel(ptaMode)} Hz</div>
            <table style="border-collapse:collapse; font-size:13px; width:100%;">
              <thead><tr><th style="border:1px solid #999; padding:3px 6px;">PTP</th><th style="border:1px solid #999; padding:3px 6px; color:#C4453C;">OD</th><th style="border:1px solid #999; padding:3px 6px; color:#2563A6;">OI</th></tr></thead>
              <tbody>
                <tr><td style="border:1px solid #999; padding:3px 6px;">V. Aérea</td><td style="border:1px solid #999; padding:3px 6px; text-align:center; color:#C4453C; font-weight:700;">${ptaAirOd !== null ? ptaAirOd : 'No calculable'}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center; color:#2563A6; font-weight:700;">${ptaAirOi !== null ? ptaAirOi : 'No calculable'}</td></tr>
                <tr><td style="border:1px solid #999; padding:3px 6px;">V. Ósea</td><td style="border:1px solid #999; padding:3px 6px; text-align:center; color:#C4453C; font-weight:700;">${ptaBoneOd !== null ? ptaBoneOd : 'No calculable'}</td><td style="border:1px solid #999; padding:3px 6px; text-align:center; color:#2563A6; font-weight:700;">${ptaBoneOi !== null ? ptaBoneOi : 'No calculable'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style="font-size:15px; font-weight:700; text-decoration:underline; margin:14px 0 6px;">LOGOAUDIOMETRÍA${exam.logo_test_type ? ' — ' + esc(exam.logo_test_type) : ''}</div>
      <table style="border-collapse:collapse; font-size:13.5px; width:100%; max-width:480px; margin-bottom:12px;">
        <thead><tr>
          <th style="border:1px solid #999; padding:5px 9px;">Discriminación</th>
          <th style="border:1px solid #999; padding:5px 9px;">%</th>
          <th style="border:1px solid #999; padding:5px 9px;">dB</th>
          <th style="border:1px solid #999; padding:5px 9px;">MKG</th>
        </tr></thead>
        <tbody>
          <tr>
            <td style="border:1px solid #999; padding:5px 9px; font-weight:700; color:#C4453C;">OD</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#C4453C; font-weight:700;">${logoOd.percent != null ? logoOd.percent : '—'}</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#C4453C; font-weight:700;">${logoOd.db != null ? logoOd.db : '—'}</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#2563A6; font-weight:700;">${logoOd.masking != null ? logoOd.masking : '—'}</td>
          </tr>
          <tr>
            <td style="border:1px solid #999; padding:5px 9px; font-weight:700; color:#2563A6;">OI</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#2563A6; font-weight:700;">${logoOi.percent != null ? logoOi.percent : '—'}</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#2563A6; font-weight:700;">${logoOi.db != null ? logoOi.db : '—'}</td>
            <td style="border:1px solid #999; padding:5px 9px; text-align:center; color:#C4453C; font-weight:700;">${logoOi.masking != null ? logoOi.masking : '—'}</td>
          </tr>
        </tbody>
      </table>
      <div style="font-size:11.5px; color:#666; margin-bottom:9px;">${['Monosílabos', 'Bisílabos', 'Términos conocidos']
        .map((t) => `${exam.logo_test_type === t ? '☑' : '☐'} ${t}`)
        .join('&nbsp;&nbsp;&nbsp;')} — el MKG se indica en el color del oído contralateral.</div>
      <div style="max-width:560px;">
        ${hasLogo ? speechSVG : '<div style="border:1px solid #ccc; height:160px; display:flex; align-items:center; justify-content:center; color:#999; font-size:12px;">Sin curva registrada</div>'}
      </div>

      <div style="font-size:14px; font-weight:700; margin-top:14px;">Observaciones:</div>
      <div style="font-size:13px; border-bottom:1px solid #999; min-height:22px; margin-top:4px;">${exam.notes ? esc(exam.notes) : ''}</div>
      <div style="font-size:13px; border-bottom:1px solid #999; min-height:22px;">&nbsp;</div>

      ${signatureBlockHTML}
    </div>
  `;

  return { title: `Audiometria - ${formatPatientName(p) || 'Paciente'} - ${exam.date}`, html };
}

export function printExamReport(exam, logoUrl, signatureUrl) {
  const { title, html } = buildExamReportHTML(exam, logoUrl, signatureUrl);
  // `fitOnePage` (22-sep-2026, pedido de Nicolás): el informe de
  // audiometría SIEMPRE debe entrar en una sola hoja A4, sin importar
  // cuántos datos tenga cargados el examen — ver print.js.
  printHTML(title, html, { fitOnePage: true });
}
